import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import usePageMeta from "@/hooks/usePageMeta";
import { landingPathForSlug, searchLandingPages } from "@/data/searchLandingPages";

const CONTACT_URL = "mailto:info@mayr-technologies.com";

const SearchLandingPage = ({ kind }) => {
  const { slug } = useParams();
  const page = searchLandingPages[slug];
  const validPage = page && page.kind === kind ? page : null;
  const canonical = validPage
    ? `https://mayr-technologies.com${landingPathForSlug(slug)}`
    : "https://mayr-technologies.com/";
  usePageMeta(validPage?.metaTitle, validPage?.metaDescription, {
    canonical,
    schema: validPage ? {
      "@context": "https://schema.org",
      "@type": validPage.kind === "product" ? "Product" : "Service",
      name: validPage.title,
      description: validPage.metaDescription,
      url: canonical,
      brand: { "@type": "Brand", name: "Mayr Technologies" },
      provider: {
        "@type": "Organization",
        name: "Mayr Technologies",
        url: "https://mayr-technologies.com/",
      },
    } : null,
  });
  if (!validPage) return <Navigate to="/" replace />;

  const pageContent = validPage;

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <Navbar />
      <main>
        <section className="px-6 md:px-8 pt-44 md:pt-52 pb-20 border-b border-white/[0.06]">
          <div className="max-w-5xl mx-auto">
            <Link to="/#solutions" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-neutral-500 hover:text-white transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> All products and solutions
            </Link>
            <p className="mt-12 text-xs uppercase tracking-[0.25em] text-neutral-500 font-['Manrope']">{pageContent.eyebrow}</p>
            <h1 className="mt-5 max-w-4xl font-['Outfit'] text-4xl md:text-6xl lg:text-7xl leading-[1.04] font-medium tracking-tight" data-testid="search-landing-title">
              {pageContent.title}
            </h1>
            <p className="mt-7 max-w-3xl text-lg md:text-xl leading-relaxed text-neutral-400 font-['Manrope']">{pageContent.summary}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href={CONTACT_URL} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black text-sm font-semibold" data-testid="landing-contact-cta">
                {pageContent.cta} <ArrowUpRight className="w-4 h-4" />
              </a>
              {pageContent.externalUrl && (
                <a href={pageContent.externalUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/[0.15] text-sm text-white hover:bg-white/[0.05]" data-testid="landing-product-cta">
                  Visit the platform <ArrowUpRight className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        </section>

        <section className="px-6 md:px-8 py-20 md:py-28">
          <div className="max-w-5xl mx-auto grid md:grid-cols-[0.8fr_1.2fr] gap-12 md:gap-20">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-neutral-600">Designed for</p>
              <h2 className="mt-4 text-3xl md:text-4xl font-medium font-['Outfit']">A practical fit for the operation.</h2>
              <p className="mt-5 text-neutral-500 leading-relaxed font-['Manrope']">{pageContent.audience}</p>
            </div>
            <div className="space-y-3">
              {pageContent.benefits.map((benefit) => (
                <div key={benefit} className="flex gap-3 rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
                  <CheckCircle2 className="w-5 h-5 text-neutral-400 shrink-0 mt-0.5" />
                  <p className="text-sm leading-relaxed text-neutral-300 font-['Manrope']">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 md:px-8 py-20 md:py-28 bg-white/[0.015] border-y border-white/[0.05]">
          <div className="max-w-5xl mx-auto">
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-600">How we approach it</p>
            <h2 className="mt-4 text-3xl md:text-4xl font-medium font-['Outfit']">From business goal to working system.</h2>
            <div className="mt-10 grid md:grid-cols-2 gap-4">
              {pageContent.process.map((step, index) => (
                <div key={step} className="rounded-xl border border-white/[0.07] p-5">
                  <span className="text-xs text-neutral-600">0{index + 1}</span>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-300 font-['Manrope']">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 md:px-8 py-20 md:py-28">
          <div className="max-w-5xl mx-auto rounded-3xl border border-white/[0.08] bg-white/[0.025] p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-medium font-['Outfit']">Ready to turn this into a measurable business result?</h2>
            <p className="mt-4 max-w-2xl text-neutral-500 font-['Manrope']">Tell us what you want the customer or team to do. We will map the shortest practical path from the current process to a working solution.</p>
            <a href={CONTACT_URL} className="mt-7 inline-flex items-center gap-2 text-sm text-white border-b border-white/30 pb-1 hover:border-white">
              {pageContent.cta} <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SearchLandingPage;
